import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { ToastProvider } from './components/Toast';
import { ModalProvider, useModal } from './components/Modal';
import { AccountProvider, useAccount } from './contexts/AccountContext';
import {
  FaTachometerAlt, FaGasPump, FaUsers, FaExchangeAlt,
  FaTruck, FaSignOutAlt, FaEye, FaEyeSlash, FaBroadcastTower
} from 'react-icons/fa';
import Dashboard    from './pages/Dashboard';
import FuelLevels   from './pages/FuelLevels';
import Users        from './pages/Users';
import Transactions from './pages/Transactions';
import Tankers      from './pages/Tankers';
import LiveFueling  from './pages/LiveFueling';
import './App.css';
import './styles/SignIn.css';
import { useState } from 'react';

/* ─── DUMMY AUTH ────────────────────────────────────────────────── */
// Any non-empty credentials work — no validation

/* ─── SIGN-OUT CONFIRM ──────────────────────────────────────────── */
function SignoutConfirm({ closeModal, onSignout }) {
  return (
    <div className="delete-confirmation">
      <p><strong>Are you sure you want to sign out?</strong></p>
      <p className="warning-text">You will need to sign in again.</p>
      <div className="modal-actions">
        <button className="btn btn-danger" onClick={() => { onSignout(); closeModal(); }}>Sign Out</button>
        <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
      </div>
    </div>
  );
}

/* ─── LAYOUT (authenticated) ────────────────────────────────────── */
function Layout({ children }) {
  const location = useLocation();
  const { account, clearAccount } = useAccount();
  const { openModal } = useModal();

  const nav = [
    { to: '/',             icon: <FaTachometerAlt />, label: 'Dashboard' },
    { to: '/fuel-levels',  icon: <FaGasPump />,       label: 'Fuel Levels' },
    { to: '/transactions', icon: <FaExchangeAlt />,   label: 'Transactions' },
    { to: '/tankers',      icon: <FaTruck />,          label: 'Tankers' },
    { to: '/live-fueling', icon: <FaBroadcastTower />,  label: 'Live Fueling' },
    { to: '/users',        icon: <FaUsers />,          label: 'Users' },
  ];

  const isActive = (to) => to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  const handleSignout = () => {
    openModal('Sign out', <SignoutConfirm onSignout={clearAccount} />, 'sm');
  };

  return (
    <div className="app-container">
      <nav className="sidebar">
        <div className="sidebar-header">
          <div className="logo">
            <span className="logo-text">✈ UAS<span className="logo-accent">Admin</span></span>
          </div>
        </div>

        <div className="nav-menu">
          <div className="nav-title">Operations</div>
          {nav.map(item => (
            <Link key={item.to} to={item.to} className={`nav-item ${isActive(item.to) ? 'active' : ''}`}>
              <span className="nav-icon">{item.icon}</span>
              <span>{item.label}</span>
            </Link>
          ))}
        </div>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">{account?.username?.charAt(0).toUpperCase()}</div>
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{account?.username}</div>
              <div className="sidebar-user-role">Admin</div>
            </div>
          </div>
          <button className="signout-btn" onClick={handleSignout} title="Sign out">
            <FaSignOutAlt />
          </button>
        </div>
      </nav>

      <main className="main-content">
        {children}
      </main>
    </div>
  );
}

/* ─── APP INNER (handles auth check) ───────────────────────────── */
function AppInner() {
  const { account, updateAccount } = useAccount();


  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSignIn = (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) return;
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      updateAccount({ username: username.trim(), token: 'uas-admin-token', role: 'admin' });
    }, 500);
  };

  if (!account) {
    return (
      <div className="signin-container">
        <div className="signin-card">
          <div className="signin-header">
            <div className="logo-signin">
              <span className="logo-text large">✈ UAS<span className="logo-accent">Admin</span></span>
            </div>
            <h2>Sign in to UAS Admin</h2>
            <p>Fuel management &amp; operations dashboard</p>
          </div>
          <form className="signin-form" onSubmit={handleSignIn}>
            <div className="form-group">
              <label className="form-label">Username</label>
              <input type="text" className="form-input" placeholder="admin" value={username} onChange={e => setUsername(e.target.value)} required autoComplete="username" />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPwd ? 'text' : 'password'}
                  className="form-input"
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  style={{ paddingRight: 45 }}
                />
                <button type="button" onClick={() => setShowPwd(p => !p)} style={{ position:'absolute',right:12,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'#9ca3af',fontSize:18,display:'flex' }}>
                  {showPwd ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>
            <button type="submit" className="btn btn-primary signin-button" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
          <div className="signin-footer">
            <p>Enter any username &amp; password to sign in</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/"             element={<Dashboard />} />
        <Route path="/fuel-levels"  element={<FuelLevels />} />
        <Route path="/transactions" element={<Transactions />} />
        <Route path="/tankers"      element={<Tankers />} />
        <Route path="/live-fueling" element={<LiveFueling />} />
        <Route path="/users"        element={<Users />} />
      </Routes>
    </Layout>
  );
}

/* ─── ROOT ──────────────────────────────────────────────────────── */
export default function App() {
  return (
    <BrowserRouter>
      <AccountProvider>
        <ToastProvider>
          <ModalProvider>
            <AppInner />
          </ModalProvider>
        </ToastProvider>
      </AccountProvider>
    </BrowserRouter>
  );
}
